import { useOrg } from '../../api/hooks/admin.hook';
import { usePlanGate } from '../../hooks/usePlanGate';
import { LimitBadge } from '../../components/PlanGate';

export const DashboardPage = () => {
  const { data: org } = useOrg();
  const { planInfo, plan } = usePlanGate();

  if (!org) return null;

  const stats = org.stats ?? {};

  return (
    <div className="max-w-4xl">
      <h1 className="text-lg font-semibold text-neutral-900 mb-6">Dashboard</h1>

      {/* Stats grid with limits */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="Members"
          value={stats.member_count ?? 0}
          limit={planInfo.max_members}
        />
        <StatCard
          label="Projects"
          value={stats.project_count ?? 0}
          limit={planInfo.max_projects}
        />
        <StatCard
          label="Tokens this month"
          value={formatTokens(stats.tokens_this_month ?? 0)}
          limit={planInfo.monthly_token_limit}
          formatLimit={formatTokens}
        />
      </div>

      {/* Plan overview */}
      <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-6">
        <h2 className="text-sm font-medium text-neutral-700 mb-3">
          Plan &amp; Limits
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs text-neutral-400 mb-1">Current Plan</div>
            <div className="font-medium text-neutral-900 capitalize">{plan}</div>
          </div>
          <div>
            <div className="text-xs text-neutral-400 mb-1">Enforcement</div>
            <div className="font-medium text-neutral-900 capitalize">
              {planInfo.limit_enforcement}
            </div>
          </div>
          <div>
            <div className="text-xs text-neutral-400 mb-1">Audit Retention</div>
            <div className="font-medium text-neutral-900">
              {planInfo.audit_retention_days} days
            </div>
          </div>
          <div>
            <div className="text-xs text-neutral-400 mb-1">Connectors</div>
            <LimitBadge current={0} limit={planInfo.max_connectors} />
          </div>
        </div>
      </div>

      {/* Org info */}
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <h2 className="text-sm font-medium text-neutral-700 mb-3">
          Organization Details
        </h2>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-neutral-500">Name</dt>
          <dd className="text-neutral-900">{org.name}</dd>
          <dt className="text-neutral-500">Slug</dt>
          <dd className="text-neutral-900 font-mono text-xs">{org.slug}</dd>
          <dt className="text-neutral-500">Plan</dt>
          <dd className="text-neutral-900 capitalize">{org.plan}</dd>
          <dt className="text-neutral-500">Status</dt>
          <dd className="text-neutral-900 capitalize">{org.billing_status}</dd>
          <dt className="text-neutral-500">Created</dt>
          <dd className="text-neutral-900">
            {new Date(org.created_at).toLocaleDateString()}
          </dd>
        </dl>
      </div>
    </div>
  );
};

const StatCard = ({
  label,
  value,
  limit,
  formatLimit,
}: {
  label: string;
  value: string | number;
  limit?: number | null;
  formatLimit?: (n: number) => string;
}) => {
  const limitLabel =
    limit === null || limit === undefined
      ? 'unlimited'
      : formatLimit
        ? formatLimit(limit)
        : String(limit);

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-4">
      <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1">
        {label}
      </div>
      <div className="text-2xl font-semibold text-neutral-900">{value}</div>
      <div className="text-xs text-neutral-400 mt-1">
        Limit: {limitLabel}
      </div>
    </div>
  );
};

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};
